import ViewTable from 'components/ViewTable';
import {daoDelete, daoPut} from 'mrf/Dao';
import { useFormDataSelector, useFormSelector } from 'mrf/redux/ReduxHelper';
import notification, {notifyError, TYPE} from 'mrf/utils/notification';
import CheckboxField from 'mrf/view/CheckboxField';
import { Col, Grid } from 'mrf/view/Grid';
import Paper from 'mrf/view/Paper';
import PageTitle from 'mrf/view/Title';
import UserRoleView from './UserRoleView';
import { languageMap } from 'mrf/view/UserProfileTemplate';
import Button from "../../../mrf/view/Button";
import {useTranslation} from "react-i18next";
import IFAuth from "../../../mrf/view/IFAuth";
import UserCountry from 'components/system/user/UserCountry';
import useCurrentUser from 'mrf/utils/useCurrentUser';
import { useMemo } from 'react';
import role from 'role';
import PropTypes from 'prop-types';
import UserInstitution from 'components/system/user/UserInstitution';


export default function EditUserView({ match }) {
    const { t } = useTranslation();

    const userId = match?.params?.id;

    const currentUser = useCurrentUser();

    const isAdmin = useMemo(() => !!currentUser.roles.find(ur => ur.role.code === 'ADMIN'), [currentUser]);

    const context = useFormSelector({
        id: userId,
        resource: 'users',
        endpoint: `/api/users/${userId}`,
        blankForm: () => isAdmin ? undefined: { countryId: 0 },
    });

    const data = useFormDataSelector(context);

    const handleToggleIsActive = async e => {
        e.stopPropagation();

        try {
            if (e.target.checked) {
                const ret = await daoPut(`/api/users/${userId}/activate`, {});
                notification({ type: TYPE.SUCCESS, message: ret?.message });
            } else {
                const ret = await daoPut(`/api/users/${userId}/deactivate`, {});
                notification({ type: TYPE.SUCCESS, message: ret?.message });
            }
        } catch (e) {
            console.error(e);
        }
    };
    
    const handleRemoveUser2FA = async e =>{
        e.stopPropagation();
        
        try{
            if(window.confirm(t("Do you really want to remove 2FA from this user?"))){
                const ret = await daoDelete(`/api/users/${userId}/removeTotp`, {})
                notification({ type: TYPE.SUCCESS, message: ret?.message })
            }
        } catch (e2) {
            console.error(e2);
            notifyError(t("Failed to remove 2FA from user"), e2,);
        }
    }

    const languageDict = languageMap.reduce((output, mapItem) => {
        output[mapItem.code] = mapItem.name;
        return output
    }, {});

    return (
        <Paper>
            <PageTitle>{data?.userName}</PageTitle>
            <Grid>
                <Col size={12}>
                    <CheckboxField
                        label='Active User'
                        field='isActive'
                        context={context}
                        onClick={handleToggleIsActive}
                    />
                </Col>
                <Col size={12}>
                    <ViewTable data={data} fields={[
                        { name: 'Full name',        key: 'fullName' },
                        { name: 'Username',         key: 'userName' },
                        { name: 'Email',            key: 'email' },
                        { name: 'Language',         key: 'language',       render: languageCode => languageDict[languageCode], translate: true },
                        { name: 'Email Confirmed?', key: 'emailConfirmed', render: confirmed => confirmed ? 'Yes' : 'No',      translate: true },
                        { name: 'Two Factor enabled?', key: 'isTwoFactorEnabled', render: enabled => enabled ? 'Yes' : 'No',      translate: true},
                    ]}/>
                </Col>
                <Col size={6}>
                    <IFAuth roleCodes={[role.ADMIN]}>
                        <UserCountry userId={userId} />
                    </IFAuth>
                </Col>
                <Col size={6}>
                    <IFAuth roleCodes={[role.ADMIN]}>
                        <UserInstitution userId={userId} />
                    </IFAuth>
                </Col>
                <Col size={12}>
                    <UserRoleView userId={userId} />
                </Col>
                <Col size={6}>
                    <Col size={6}>
                        <IFAuth roleCodes={[role.ADMIN]} condition={data?.isTwoFactorEnabled}>
                            <Button
                                className='mt-8'
                                type='button'
                                color='delete'
                                onClick={handleRemoveUser2FA}
                            >
                                Remove two-factor authentication from user
                            </Button>
                        </IFAuth>
                    </Col>
                </Col>
            </Grid>
        </Paper>
    );
}

EditUserView.propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string,
      }),
    }).isRequired,
};